/**
 * displays store locations statewise on page
 *
 * @module views/components/IndexedStoreCard
 * @memberof -Common
 */
import './IndexedStoreCard.scss';

import React, { useEffect, useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';

import * as utils from '@ulta/hooks/useAnchorList/useAnchorList';

import { ANCHOR_LIST_HEIGHT } from '../BrandListSection/BrandListSection';
import StoreListItem from '../StoreListItem/StoreListItem';

/**
 * Represents a IndexedStoreCard component
 *
 * @method
 * @param { IndexedStoreCardProps} props - React properties passed from composition
 * @returns IndexedStoreCard
 */
export const IndexedStoreCard = ( props )=> {
  const { indexedStores, oddBackgroundColor, evenBackgroundColor, storeAccessibilityLabel, store } = props;
  const sectionEle = useRef();
  const { breakpoint } = useDeviceInflection();
  const id = `${indexedStores?.title}` === '#' ? `Hashsection` : `${indexedStores?.title}section`;
  const navEle = useRef();
  useEffect( () => {
    document.addEventListener( 'scroll', highlightOnScroll );
    sectionEle.current = document.getElementById( id );
    navEle.current = document.getElementsByClassName( 'AnchorList' )[0];
    return () => {
      document.removeEventListener( 'scroll', highlightOnScroll );
    };
  }, [] );

  // highlights the selected letter on scroll
  const highlightOnScroll = () => {
    if( !props.reportActive || !navEle.current || !sectionEle.current ){
      return;
    }

    const itemSection = sectionEle.current?.getBoundingClientRect()?.top;
    const nav = navEle.current?.getBoundingClientRect()?.top;
    if( itemSection <= nav + ANCHOR_LIST_HEIGHT && itemSection >= nav ){
      props.onScrolled && props.onScrolled( indexedStores.title );
    }
  };

  if( !store ){
    return null;
  }
  // logic to render store items list
  const colRenderer = ( items )=>{
    return items.map( ( card, index ) => (
      <li
        className={ classNames( `IndexedStoreCard--storeCard ${( index + 1 ) % 2 === 0 ? evenBackgroundColor : oddBackgroundColor}`
        ) }
        key={ index }
        aria-label={ storeAccessibilityLabel }
        role='group'
      >
        <StoreListItem
          name={ card.cityName }
          address1={ card.address1 }
          address2={ card.address2 }
          hideRadioButton={ true }
          storeName={ card.storeName }
          isEnabled={ true }
          storeId={ '' }
        />
      </li>

    ) );
  };

  return (
    <div className='IndexedStoreCard--wrapper'>
      { utils.ColumnSort( { items:store, breakpoint }, { colRenderer, getNoOfColumns } ) }
    </div>
  );
};

/**
 * Handles on letter click
 * @method getNoOfColumns
 * @param {*} breakpoint
 * @returns number of columns as per screen inflection
 */
export const getNoOfColumns = ( breakpoint ) => {
  switch ( breakpoint ){
    case 'XL':
    case 'LG':
      return 3;
    case 'MD':
      return 2;
    default:
      return 1;
  }
};

/**
 * Property type definitions
 * @typedef IndexedStoreCardProps
 * @type {object}
 * @property {object} store - List of All stores
 * @property {object} indexedStore - List of All stores with state names
 * @property {string} oddBackgroundColor - sets background color for cards
 * @property {string} evenBackgroundColor -  sets background color for cards
 * @property {string} dividerColor - Sets divider color
 * @property {string} storeAccessibilityLabel - sets the aria Label of all the stores
 */
export const propTypes =  {
  store:PropTypes.object,
  oddBackgroundColor:PropTypes.string,
  evenBackgroundColor:PropTypes.string,
  storeAccessibilityLabel:PropTypes.string,
  indexedStore:PropTypes.object
};


IndexedStoreCard.propTypes = propTypes;

export default IndexedStoreCard;
